import { useState, useEffect } from "react";
import {
  Avatar,
  Button,
  Input,
  Select,
  DatePicker,
  Upload as UploadAntd,
  message,
  Form,
} from "antd";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import CameraIcon from "../../assets/images/profile-detail-icon/camera-icon.svg";
import UserIcon from "../../assets/images/input-icons/user-icon.svg";
import GenderIcon from "../../assets/images/input-icons/gander-icon.svg";
import EmailIcon from "../../assets/images/input-icons/mail-icon.svg";
import CalendarIcon from "../../assets/images/input-icons/calendar-icon24.svg";
import BackArrowIcon from "../../assets/images/profile-detail-icon/back-page-icon.svg";
import moment from "moment";
import { S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { ShowToast, Severty } from "../../helper/toast";

const s3Config = {
  region: 'us-east-1',
  credentials: {
    accessKeyId: "AKIAFBC97UM4I3ILWTIKQ",
    secretAccessKey: "vMUNWcZvMesuFfmUwdUYnsaOr1pp38wu8nOon8eb",
  },
  endpoint: "https://s3-noi.aces3.ai/",
  forcePathStyle: true,
  requestChecksumCalculation: "WHEN_REQUIRED",
  responseChecksumValidation: "WHEN_REQUIRED",
};
const s3Client = new S3Client(s3Config);

const { Option } = Select;

function ProfileIndex() {
  const [isEditing, setIsEditing] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  const FileType = ["image/png", "image/jpg", "image/jpeg", "image/avif", "image/webp", "image/gif"];
  const [profile, setProfile] = useState({
    name: '',
    dob: '',
    gender: '',
    profileImage: null,
    email: ''
  });

  const uploadFileToS3 = async (file, bucketName) => {
    const key = "taxi/profile/" + file.name;
    const params = {
      Bucket: bucketName,
      Key: key,
      Body: file,
    };

    const upload = new Upload({
      client: s3Client,
      params,
    });

    try {
      const data = await upload.done();
      return data;
    } catch (err) {
      throw err;
    }
  };

  const beforeUpload = (file) => {
    if (!FileType.includes(file.type)) {
      message.error("File format is not correct");
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 5;
    if (!isLt2M) {
      message.error("Image must be smaller than 5 MB!");
      return false;
    }
    return true;
  };

  const handleChange = async (info) => {
    const { file } = info;
    if (!file) return;
    try {
      if (!beforeUpload(file)) return;

      setLoading(true);
      setFile([{ ...file, status: 'uploading' }]);

      const data = await uploadFileToS3(file, 'invent-co-new');

      if (data?.Location) {
        setFile([{ uid: file.uid, name: file.name, status: 'done', url: data.Location }]);
        form.setFieldsValue({ profileImage: data.Location });
        setImageUrl(data.Location);
      } else {
        message.error("Upload failed. Please try again.");
        setFile([]);
      }
    } catch (err) {
      console.error("Upload Error:", err);
      message.error("Error uploading file.");
      setFile([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    request({
      url: apiPath.profile,
      method: "GET",
      onSuccess: (data) => {
        const profileData = data.data;
        setProfile(profileData);

        form.setFieldsValue({
          name: profileData.name,
          gender: profileData.gender,
          profileImage: profileData.profileImage,
          dob: profileData.dob ? moment(profileData.dob) : null,
        });

        if (profileData.profileImage) {
          setImageUrl(profileData.profileImage);
        }

        setLoading(false);
      },
      onError: () => {
        message.error("Failed to load profile data");
        setLoading(false);
      }
    });
  }, [refresh]);

  const sendUpdateRequest = async (values) => {
    const { name, dob, gender, profileImage, voiceMessagePrefrence } = values;
    const payload = {
      name,
      dob,
      gender,
      profileImage: profileImage || profile.profileImage,
      voiceMessagePrefrence: voiceMessagePrefrence || "female",
    };
    try {
      request({
        url: apiPath.updateProfile,
        method: "PUT",
        data: payload,
        onSuccess: (data) => {
          setProfile(data.data);
          setImageUrl(data.data.profileImage);
          ShowToast(data.message, Severty.SUCCESS);
          setIsEditing(false);
          setRefresh(!refresh);
        },
        onError: () => {
          message.error("Failed to update profile");
        }
      });
    } catch (error) {
      console.error("Profile Update Error:", error);
      message.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-container">
      {isEditing ? (
        <>
          <Button
            className="back-button profile-title"
            onClick={() => setIsEditing(false)}
          >
            <img src={BackArrowIcon} alt="Back" /> Back
          </Button>
          <div className="profile-detail-card01">
            <Form
              form={form}
              layout="vertical"
              onFinish={sendUpdateRequest}
            >
              <Form.Item name="profileImage">
                <div className="profile-header-imager">
                  <Avatar
                    src={imageUrl || "https://via.placeholder.com/120"}
                    alt={profile.name || "User"}
                    className="profile-avatar"
                  />
                  <div className="camera-icon">
                    <UploadAntd
                      className="upload-picar"
                      name="profileImage"
                      maxCount={1}
                      beforeUpload={beforeUpload}
                      customRequest={handleChange}
                      fileList={file}
                    >
                      <Button>
                        <img alt="" src={CameraIcon} />
                      </Button>
                    </UploadAntd>
                  </div>
                </div>
              </Form.Item>
              <Form.Item
                name="name"
                label="Enter your full name"
                rules={[
                  { required: true, message: "Name is required" },
                  { max: 200, message: "Max 200 characters" },
                  { min: 2, message: "At least 2 characters" },
                ]}
              >
                <Input
                  placeholder="Enter Full Name"
                  prefix={<img src={UserIcon} alt="User Icon" />}
                />
              </Form.Item>
              <Form.Item
                name="gender"
                label="Select Gender"
                rules={[{ required: true, message: 'Please select your gender' }]}
              >
                <Select
                  placeholder="Select Gender"
                  suffixIcon={<img src={GenderIcon} alt="Gender Icon" />}
                >
                  <Option value="male">Male</Option>
                  <Option value="female">Female</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="dob"
                label="Enter date of birth"
              >
                <DatePicker
                  format="DD MMMM YYYY"
                  className="date-picker-profile"
                  style={{ width: "100%" }}
                  allowClear={false}
                />
              </Form.Item>
              <Form.Item
                name="voiceMessagePrefrence"
                label="Select Whatsapp Voice Message Gender"
                rules={[{ required: true, message: 'Please select whatsapp voice message gender' }]}
              >
                <Select
                  placeholder="Select Voice Message Gender"
                  suffixIcon={<img src={GenderIcon} alt="Gender Icon" />}
                >
                  <Option value="male">Male</Option>
                  <Option value="female">Female</Option>
                  {/* <Option value="other">Other</Option > */}
                </Select>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  className="fill-btn"
                  htmlType="submit"
                  loading={loading}
                >
                  Save
                </Button>
              </Form.Item>
            </Form>
          </div>
        </>
      ) : (
        <>
          <h2 className="profile-title">My Profile</h2>
          <div className="profile-detail-card01">
            <div className="profile-list-details">
              <div className="edit-profile-avtar">
                <Avatar
                  src={imageUrl || "https://via.placeholder.com/120"}
                  className="profile-avatar"
                />
              </div>
              <ul>
                <li>
                  <div className="detail-pro-icons">
                    <img src={UserIcon} alt="User Icon" />
                  </div>
                  <div className="text-detail-names">
                    <h6>Full Name</h6>
                    <p>{profile.name || "Not provided"}</p>
                  </div>
                </li>
                <li>
                  <div className="detail-pro-icons">
                    <img src={GenderIcon} alt="Gender Icon" />
                  </div>
                  <div className="text-detail-names">
                    <h6>Gender</h6>
                    <p>{profile.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : "Not provided"}</p>
                  </div>
                </li>
                <li>
                  <div className="detail-pro-icons">
                    <img src={CalendarIcon} alt="Calendar Icon" />
                  </div>
                  <div className="text-detail-names">
                    <h6>Date of Birth</h6>
                    <p>{profile.dob ? moment(profile.dob).format("DD MMMM YYYY") : "Not provided"}</p>
                  </div>
                </li>
                <li>
                  <div className="detail-pro-icons">
                    <img src={EmailIcon} alt="Email Icon" />
                  </div>
                  <div className="text-detail-names">
                    <h6>Email Address</h6>
                    <p>
                      {profile.email
                        ? profile.email.replace(/^(.{3}).+(@.+)$/, "$1****$2")
                        : "Not provided"}
                    </p>
                  </div>
                </li>
                <li>
                  <div className="detail-pro-icons">
                    <img src={GenderIcon} alt="Gender Icon" />
                  </div>
                  <div className="text-detail-names">
                    <h6>WhatsApp Voice Message Gender</h6>
                    <p>{profile?.voiceMessagePrefrence ? profile.voiceMessagePrefrence.charAt(0).toUpperCase() + profile.voiceMessagePrefrence.slice(1) : "Not provided"}</p>
                  </div>
                </li>
              </ul>
            </div>
            <Button
              type="primary"
              className="fill-btn"
              onClick={() => {
                setIsEditing(true);
                form.setFieldsValue({
                  name: profile.name,
                  gender: profile.gender,
                  profileImage: profile.profileImage,
                  dob: profile.dob ? moment(profile.dob) : null,
                  voiceMessagePrefrence: profile.voiceMessagePrefrence,
                });
              }}
            >
              Edit
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default ProfileIndex;
